import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ChevronRight, Layers } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/cn';
import { relativeTime } from '@/lib/format';
import {
  CLASSIFICATION_META,
  TAG_LABELS,
  avatarColor,
  type Bundle,
} from '@/lib/inbox';
import { MessageRow } from './MessageRow';

interface Props {
  bundle: Bundle;
  selectedId: string | null;
  onSelectMessage: (id: string) => void;
}

export function BundleRow({ bundle, selectedId, onSelectMessage }: Props) {
  const [expanded, setExpanded] = useState(false);
  const meta = CLASSIFICATION_META[bundle.classification];
  const count = bundle.messages.length;
  const senderLabel =
    bundle.fromName || bundle.fromEmail.split('@')[0] || bundle.fromEmail;

  return (
    <div className="rounded-md border border-border bg-card">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center gap-3 px-3 py-2 text-left transition-colors hover:bg-accent/20"
      >
        <div
          className="flex h-8 w-8 flex-none items-center justify-center rounded-full text-xs font-medium text-white"
          style={{ background: avatarColor(bundle.fromEmail) }}
        >
          <Layers className="h-3.5 w-3.5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="truncate text-sm font-medium">
              {count} from {senderLabel}
            </p>
            <Badge variant={meta.variant}>{meta.label}</Badge>
            {bundle.tags.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {bundle.tags.slice(0, 2).map((t) => (
                  <Badge key={t} variant="outline" className="text-[10px]">
                    {TAG_LABELS[t] ?? t}
                  </Badge>
                ))}
              </div>
            )}
          </div>
          <p className="mt-0.5 truncate text-xs text-muted-foreground">
            most recent {relativeTime(bundle.mostRecentAt)} · click to expand
          </p>
        </div>
        <div className="flex-none text-muted-foreground">
          {expanded ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
        </div>
      </button>
      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className={cn('overflow-hidden border-t border-border/60')}
          >
            <motion.ul
              initial="hidden"
              animate="visible"
              variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.02 } } }}
              className="space-y-1 p-2"
            >
              {bundle.messages.map((m) => (
                <li key={m.id}>
                  <MessageRow
                    message={m}
                    selected={selectedId === m.id}
                    onSelect={() => onSelectMessage(m.id)}
                    compact
                  />
                </li>
              ))}
            </motion.ul>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
